<script setup lang="ts">
import type { Component } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseInput from "@/components/ui/BaseInput/BaseInput.vue";
import { cockpitRegisterUrl } from "@/components/utils/cockpitUrls";
import { errorToString } from "@/error/errorUnwrapper";
import { useUserStore } from "@/store/user";
import { eventTarget } from "@/utils/dom/dom";
import { isCloud } from "@/utils/env/Environment";
import { firstQueryParam } from "@/utils/vue/queryParam";

import { login } from "./LoginPage.utils";
import LoginPageTenants from "./LoginPageTenants.vue";

const emit = defineEmits<{ pageSwitch: [page: Component] }>();

const email = ref("");
const error = ref("");

const route = useRoute();
const { t } = useI18n();

function handleInvalid(event: Event) {
  const input = eventTarget<HTMLInputElement>(event);
  error.value = input.validationMessage;
}

async function loginWithEmail() {
  error.value = "";
  try {
    const tenants = await login({
      email: email.value,
      type: firstQueryParam(route.query, "type") || "",
      path: firstQueryParam(route.query, "path") || "",
    });
    useUserStore().setLoginData(email.value, tenants);
    if (tenants && tenants.length > 1) {
      emit("pageSwitch", LoginPageTenants);
    }
  } catch (err) {
    error.value = errorToString(err, t("loginPage.notLogin"));
  }
}
</script>

<template>
  <div v-autofocus tabindex="-1" aria-labelledby="email-page-title">
    <h1 id="email-page-title" class="login">
      {{ $t("loginPageEmail.login") }}
    </h1>
    <p class="description">{{ $t("loginPageEmail.useWorkEmail") }}</p>

    <div class="form-container">
      <form @submit.prevent="loginWithEmail" @invalid.capture.prevent="handleInvalid">
        <BaseInput
          v-model="email"
          control-type="text"
          type="email"
          name="email"
          :label="$t('loginPageEmail.email')"
          autofocus
          required
        />
        <BaseButton type="submit" full-width>
          {{ $t("loginPageEmail.continue") }}
        </BaseButton>
        <span class="error">{{ error }}</span>
      </form>
      <span v-if="isCloud">
        <p class="register">
          <I18nT keypath="loginPageEmail.noAccount" scope="global">
            <template #register>
              <a :href="cockpitRegisterUrl()">
                {{ $t("loginPageEmail.noAccount.register") }}
              </a>
            </template>
          </I18nT>
        </p>
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

.login {
  @include typography.extra-large;

  margin-bottom: 8px;
}

.description {
  @include typography.medium;

  color: colors.$text-subtle;
  margin-bottom: 30px;
}

.base-button {
  margin-top: 1em;
  margin-bottom: 20px;
}

.register {
  @include typography.medium;

  color: colors.$text-subtle;

  a {
    @include typography.medium("semi-bold");

    color: colors.$text;
    text-decoration: none;
  }
}

.error {
  @include typography.small("medium");

  color: colors.$destructive;
}

#message.warn {
  background-color: colors.$destructive;
  margin-bottom: -180px;
  width: 300px;
}
</style>
